/**
 * Rate Limit Warning Component
 * Progressive alerts at thresholds (5, 3, 1 messages)
 * Non-intrusive toast-style warnings with Islamic guidance
 */

import React, { useEffect } from 'react';
import { useToastActions } from '../../../../shared/ui/toast/Toast';
import { getIslamicGuidance, getRateLimitTitle } from '../utils/islamicGuidance';
import type { RateLimitState } from '../hooks/useChatRateLimit';

interface RateLimitWarningProps {
  rateLimitState: RateLimitState | null;
  previousRemaining?: number | null;
}

/**
 * Rate Limit Warning Component
 * Shows progressive warnings using toast system
 */
export const RateLimitWarning: React.FC<RateLimitWarningProps> = ({
  rateLimitState,
  previousRemaining,
}) => {
  const { warning, info } = useToastActions();
  const lastWarningRef = React.useRef<number | null>(null);

  useEffect(() => {
    if (!rateLimitState || rateLimitState.remaining === 0) {
      return;
    }

    const { remaining, limit } = rateLimitState;
    const previous = previousRemaining ?? limit;

    // Only show warning if remaining decreased and crossed a threshold
    if (previous <= remaining) {
      return; // No change or increased (reset)
    }

    // Determine which threshold was crossed
    let threshold: number | null = null;
    let warningType: 'warning' | 'info' = 'info';

    if (previous > 5 && remaining <= 5 && remaining > 3) {
      threshold = 5;
      warningType = 'info';
    } else if (previous > 3 && remaining <= 3 && remaining > 1) {
      threshold = 3;
      warningType = 'warning';
    } else if (previous > 1 && remaining === 1) {
      threshold = 1;
      warningType = 'warning';
    }

    // Show warning if threshold crossed and not already shown for this count
    if (threshold !== null && lastWarningRef.current !== remaining) {
      lastWarningRef.current = remaining;
      
      const guidance = getIslamicGuidance('approaching_limit', remaining);
      const title = getRateLimitTitle('approaching_limit');
      
      if (warningType === 'warning') {
        warning(title, guidance, { duration: 5000 });
      } else {
        info(title, guidance, { duration: 5000 });
      }
    }
  }, [rateLimitState, previousRemaining, warning, info]);

  // Reset last warning when rate limit resets
  useEffect(() => {
    if (rateLimitState && rateLimitState.remaining === rateLimitState.limit) {
      lastWarningRef.current = null;
    }
  }, [rateLimitState]);

  // This component doesn't render anything - it just triggers toasts
  return null;
};

